import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { AppContext, Screen } from './context/AppContext';
import { useAuth, Role } from './context/AuthContext';
import { useData } from './context/DataContext';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { useGeolocation } from './hooks/useGeolocation';
import { useOrder } from './context/OrderContext';
import { AnimatePresence, motion } from 'framer-motion';

import WelcomeScreen from './screens/WelcomeScreen';
import RoleSelectionScreen from './screens/RoleSelectionScreen';
import ClientAuthScreen from './screens/ClientAuthScreen';
import ProAuthScreen from './screens/ProAuthScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import UpdatePasswordScreen from './screens/UpdatePasswordScreen';
import HomeScreen from './screens/HomeScreen';
import RestaurantsScreen from './screens/RestaurantsScreen';
import OrderScreen from './screens/OrderScreen';
import AccountScreen from './screens/AccountScreen';
import RestaurantDetailScreen from './screens/RestaurantDetailScreen';
import BottomNav from './components/BottomNav';
import CartOverlay from './components/CartOverlay';

import AdminDashboardScreen from './screens/admin/AdminDashboardScreen';
import AdminDishesScreen from './screens/admin/AdminDishesScreen';
import AdminOrdersScreen from './screens/admin/AdminOrdersScreen';
import AdminReservationsScreen from './screens/admin/AdminReservationsScreen';
import AdminAccountScreen from './screens/admin/AdminAccountScreen';
import AdminAccountingScreen from './screens/admin/AdminAccountingScreen';
import AdminDriversScreen from './screens/admin/AdminDriversScreen';
import SubscriptionScreen from './screens/admin/SubscriptionScreen';
import PaymentHistoryScreen from './screens/admin/PaymentHistoryScreen';
import AdminBottomNav from './components/AdminBottomNav';

import LivreurHomeScreen from './screens/LivreurHomeScreen';
import LivreurActiveOrderScreen from './screens/LivreurActiveOrderScreen';
import LivreurDashboardScreen from './screens/LivreurDashboardScreen';
import LivreurOrdersScreen from './screens/LivreurOrdersScreen';
import LivreurAccountingScreen from './screens/LivreurAccountingScreen';
import LivreurAccountScreen from './screens/LivreurAccountScreen';
import LivreurBottomNav from './components/LivreurBottomNav';

import PendingValidationScreen from './screens/PendingValidationScreen';
import RestaurateurOnboardingScreen from './screens/RestaurateurOnboardingScreen';
import LivreurOnboardingScreen from './screens/LivreurOnboardingScreen';
import ProfileCompletionModal from './components/ProfileCompletionModal';
import InstallPrompt from './components/InstallPrompt';
import PermissionModal from './components/PermissionModal';
import AlarmOverlay from './components/AlarmOverlay';
import SuperAdminDashboard from './screens/superadmin/SuperAdminDashboard';
import SuperAdminRestaurants from './screens/superadmin/SuperAdminRestaurants';
import SuperAdminDrivers from './screens/superadmin/SuperAdminDrivers';
import SuperAdminPayments from './screens/superadmin/SuperAdminPayments';
import SuperAdminProfile from './screens/superadmin/SuperAdminProfile';
import SuperAdminBottomNav, { SuperAdminTab } from './components/SuperAdminBottomNav';


const APP_VERSION = '1.6.7'; // Nuclear menu stability fix



const MainApp: React.FC = () => {
    const { view, setView, isCartOpen, setIsCartOpen } = useContext(AppContext);
    const { totalItems } = useOrder();
    const { 
        currentUser, 
        loading: authLoading, 
        logout, 
        isPasswordRecovery, 
        refreshProfile, 
        profileLoaded, 
        authError,
        pendingNotification,
        clearPendingNotification,
        updateUserProfile
    } = useAuth();

    // Track the last base screen to return to after detail view
    const [lastBaseScreen, setLastBaseScreen] = useState<Screen>('home');

    // Gestion du cache PWA / Détection de mise à jour / Splash Screen
    useEffect(() => {
        // Hide native splash screen immediately on mount for better performance
        if (Capacitor.isNativePlatform()) {
            SplashScreen.hide().catch(err => console.warn("SplashScreen.hide error:", err));
        }

        const storedVersion = localStorage.getItem('yumii_app_version');
        if (storedVersion !== APP_VERSION) {
            console.log(`Nouvelle version détectée: ${APP_VERSION} (ancienne: ${storedVersion}). Rafraîchissement...`);
            localStorage.setItem('yumii_app_version', APP_VERSION);
            // On attend un tout petit peu pour s'assurer que le localStorage est écrit
            setTimeout(() => {
                window.location.reload();
            }, 500);
        }
    }, []);

    const { getCurrentPosition, requestPermissions } = useGeolocation(currentUser?.uid);
    const [isPermissionsRequested, setIsPermissionsRequested] = useState(false);

    // Request location permissions on startup for Native platforms (Android/iOS)
    // as requested by the user to ensure GPS features work immediately.
    useEffect(() => {
        if (!isPermissionsRequested && Capacitor.isNativePlatform()) {
            console.log("📍 [Native] Requesting location permissions on startup...");
            requestPermissions().then((granted) => {
                if (granted) {
                    console.log("📍 [Native] Location permissions granted.");
                } else {
                    console.warn("📍 [Native] Location permissions denied on startup.");
                }
                setIsPermissionsRequested(true);
            }).catch(err => {
                console.error("📍 [Native] Error requesting permissions:", err);
                setIsPermissionsRequested(true);
            });
        }
    }, [requestPermissions, isPermissionsRequested]);

    useEffect(() => {
        if (view.screen !== 'restaurantDetail' &&
            !view.screen.startsWith('admin') &&
            view.screen !== 'forgotPassword' &&
            view.screen !== 'updatePassword') {
            setLastBaseScreen(view.screen);
        }
    }, [view.screen]);


    // Étapes de l'authentification : Welcome -> Role -> Auth Client/Pro -> ForgotPassword
    const [hasDismissedProfileModal, setHasDismissedProfileModal] = useState(() => {
        if (typeof window !== 'undefined') {
            return sessionStorage.getItem('yumii_profile_modal_dismissed') === 'true';
        }
        return false;
    });
    const [authStep, setAuthStep] = useState<'welcome' | 'role_select' | 'auth_client' | 'auth_pro' | 'forgot_password'>('welcome');

    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const [showSuccessNotification, setShowSuccessNotification] = useState(false);
    const [superAdminTab, setSuperAdminTab] = useState<SuperAdminTab>('dashboard');

    useEffect(() => {
        if (currentUser?.role === 'restaurateur' && currentUser?.restaurantStatus === 'approved' && !currentUser.hasBeenWelcomed && !hasShownWelcome) {
            // Show notification only once per user account
            setShowSuccessNotification(true);
            setHasShownWelcome(true);
            
            // Persist in Firestore to prevent repeated displays across devices/reinstalls
            updateUserProfile({ hasBeenWelcomed: true }).catch(console.error);
            
            setTimeout(() => setShowSuccessNotification(false), 5000);
        }
    }, [currentUser?.restaurantStatus, currentUser?.role, currentUser?.hasBeenWelcomed, hasShownWelcome]);

    useEffect(() => {
        // Redirection guard: if we are in the middle of a notification redirect, 
        // block any "reset to home" or "force dashboard" logic for 5 seconds.
        if (isRedirectingRef.current) {
            console.log("🛡️ [Navigation] Overrides BLOCKED during redirect window.");
            return;
        }

        if (currentUser?.role === 'restaurateur' && (currentUser.restaurantStatus === 'approved' || currentUser.restaurantStatus === 'suspended')) {
            const isAdminScreen = view.screen.startsWith('admin');
            if (!isAdminScreen) {
                console.log("👮 [Navigation] Restricting pro to admin dashboard");
                setView({ screen: 'adminDashboard' });
            }
        } else if (currentUser?.role === 'super_admin') {
            // Force Super Admin Dashboard
        } else if (currentUser?.role === 'livreur') {
            const isLivreurScreen = view.screen.startsWith('livreur');
            if (!isLivreurScreen) {
                console.log("👮 [Navigation] Restricting driver to driver dashboard");
                setView({ screen: 'livreurDashboard' });
            }
        } else if (currentUser?.role === 'client') {
            if (view.screen.startsWith('admin') || view.screen.startsWith('livreur')) {
                console.log("👮 [Navigation] Restricting client to home/orders");
                setView({ screen: 'home' });
            }
        }
    }, [currentUser, view.screen, setView]);

    const { loading: dataLoading } = useData();
    const [isInitialCheck, setIsInitialCheck] = useState(true);
    const hasResetView = useRef(false);
    const isRedirectingRef = useRef(false);

    // Centralized Notification Router
    const routeNotification = useCallback((payload: { title?: string, type?: string, role?: string, orderId?: string }) => {
        const { title, type, role, orderId } = payload;
        const normalizedTitle = (title || "").toLowerCase();

        console.log(`[Notification Router] Role: ${currentUser?.role}, Type: ${type}, Title: ${title}`);

        // Mark as redirecting to block the 'Reset to Home' effects
        isRedirectingRef.current = true;
        setTimeout(() => isRedirectingRef.current = false, 3000);

        if (currentUser?.role === 'client') {
            const clientTitles = ['commande', 'livrée', 'acceptée', 'prête', 'route', 'refusée', 'annulée', 'arrive', 'update', 'reçu'];
            
            // Strong deterministic hook: orderId almost guarantees it's an order notification.
            const hasOrderId = !!orderId;
            
            const isOrderUpdate = hasOrderId || type === 'order_update' || type === 'nudge_response' || clientTitles.some(t => normalizedTitle.includes(t));
            
            console.log(`🔎 [Route-Client] isOrderUpdate: ${isOrderUpdate}, Type: ${type}, Title: ${normalizedTitle}, OrderId: ${orderId}`);
            
            if (isOrderUpdate) {
                console.log("✅ [Navigation] Routing client to ORDERS screen");
                setView({ screen: 'orders' });
            } else {
                console.log("ℹ️ [Navigation] Non-order notification, staying on current view");
            }
        }
        else if (currentUser?.role === 'restaurateur') {
            // Priority 1: Admin Account (Subscription/Payments)
            if (type === 'payment_validated' || type === 'subscription_expired' || type === 'subscription_reminder' || type === 'featured_expired' || 
                normalizedTitle.includes('paiement') || normalizedTitle.includes('abonnement') || normalizedTitle.includes('visibilité')) {
                setView({ screen: 'adminAccount' });
                return;
            }

            // Priority 2: Order Tabs
            if (type === 'new_order' || normalizedTitle.includes('nouvelle commande')) {
                setView({ screen: 'adminOrders', params: { initialTab: 'pending' } });
            }
            else if (type === 'nudge' || normalizedTitle.includes('relance')) {
                setView({ screen: 'adminOrders', params: { initialTab: 'sent' } });
            }
            else if (type === 'mission_accepted' || type === 'mission_rejected' || normalizedTitle.includes('mission acceptée') || normalizedTitle.includes('mission refusée')) {
                setView({ screen: 'adminOrders', params: { initialTab: 'in_progress' } });
            }
            else if (type === 'order_arrived' || type === 'order_confirmed' || type === 'new_rating' || type === 'manual_rating' ||
                     normalizedTitle.includes('arrivée') || normalizedTitle.includes('réception confirmée') || normalizedTitle.includes('avis') || normalizedTitle.includes('note')) {
                setView({ screen: 'adminOrders', params: { initialTab: 'completed' } });
            }
        }
        else if (currentUser?.role === 'livreur') {
            if (type === 'driver_assigned' || normalizedTitle.includes('nouvelle mission')) {
                setView({ screen: 'livreurHome' });
            }
            else if (type === 'order_confirmed' || type === 'new_rating' || normalizedTitle.includes('confirmée') || normalizedTitle.includes('avis') || normalizedTitle.includes('note')) {
                setView({ screen: 'livreurOrders' });
            }
        }
        else if (currentUser?.role === 'admin') {
            if (type === 'new_registration') {
                if (role === 'livreur' || normalizedTitle.includes('livreur')) {
                    setSuperAdminTab('drivers');
                } else {
                    setSuperAdminTab('restaurants');
                }
                setView({ screen: 'superAdmin' });
            }
            else if (type === 'new_payment' || normalizedTitle.includes('paiement')) {
                setSuperAdminTab('payments');
                setView({ screen: 'superAdmin' });
            }
        }
    }, [currentUser, setView, setSuperAdminTab]);

    useEffect(() => {
        // [SAFETY] Force splash screen to clear after 5 seconds no matter what
        const safetyWatcher = setTimeout(() => {
            if (isInitialCheck) {
                console.warn("⚠️ [MainApp] Initial check timed out. Forcing splash screen clear.");
                setIsInitialCheck(false);
            }
        }, 5000);

        // Regular logic
        // Only stop the initial check if auth is not loading AND (we have no user OR the role is fetched)
        const canFinishCheck = !authLoading && (!currentUser || currentUser.roleFetched);

        if (canFinishCheck) {
            // Wait slightly longer to ensure profile state is fully propagated
            const timer = setTimeout(() => {
                console.log("🔓 [MainApp] isInitialCheck -> false");
                setIsInitialCheck(false);
            }, 800);
            return () => {
                clearTimeout(timer);
                clearTimeout(safetyWatcher);
            };
        }

        return () => clearTimeout(safetyWatcher);
    }, [authLoading, currentUser?.roleFetched, currentUser === null, isInitialCheck]);

    useEffect(() => {
        if (!authLoading && currentUser) {
            // Handle Deep Linking (Notification click)
                const urlParams = new URLSearchParams(window.location.search);
                const orderId = urlParams.get('orderId');
                const type = urlParams.get('type');
                const role = urlParams.get('role');
                const uid = urlParams.get('uid');
                
                if (orderId || type) {
                    console.log(`🔗 Deep Link Detected - OrderId: ${orderId}, Type: ${type}`);
                    // Clean URL while preserving history structure
                    window.history.replaceState(view, document.title, "/");
                    
                    // Mark as redirecting to prevent any other logic from interfering
                    isRedirectingRef.current = true;
                    setTimeout(() => isRedirectingRef.current = false, 5000);

                    // Use centralized router for deep link
                    routeNotification({ title: '', type: type || '', role: role || '', orderId: orderId || '' });
                }
            }
    }, [authLoading, currentUser, setView, routeNotification]);

    // Handle Persistent Notifications (Cold Start / Pre-mount catch)
    useEffect(() => {
        if (pendingNotification && currentUser && profileLoaded) {
            console.log("📍 [Navigation] Processing persistent notification:", pendingNotification);
            
            // Mark as redirecting to prevent Home overrides
            isRedirectingRef.current = true;
            setTimeout(() => {
                isRedirectingRef.current = false;
                console.log("🛡️ [Navigation] Redirect window CLOSED.");
            }, 5000);

            routeNotification(pendingNotification);
            clearPendingNotification();
        }
    }, [pendingNotification, currentUser, profileLoaded, routeNotification, clearPendingNotification]);

    const lastRedirectRef = useRef<number>(0);
    // Handle Global Notification Redirection (Systematic)
    useEffect(() => {
        const handleNotification = (event: any) => {
            const { title, type, role, orderId } = event.detail;

            // Prevent redirect loops or spam (max 1 redirect per 3 seconds)
            const now = Date.now();
            if (now - lastRedirectRef.current < 3000) return;
            lastRedirectRef.current = now;

            routeNotification({ title, type, role, orderId });
        };

        window.addEventListener('yumii:notification', handleNotification);
        return () => window.removeEventListener('yumii:notification', handleNotification);
    }, [currentUser, routeNotification]);

    // Force Home on App Resume (for Clients)
    useEffect(() => {
        const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
            if (isActive && currentUser?.role === 'client') {
                // Diagnostic: Home reset disabled to check notification routing
                console.log('🔄 [Navigation] App resumed. Home reset is currently DISABLED for testing.');
            }
        };

        const listenerPromise = App.addListener('appStateChange', handleAppStateChange);
        return () => {
            listenerPromise.then(l => l.remove());
        };
    }, [currentUser, setView, view.screen]);

    // Handle Native Push Notification Actions
    useEffect(() => {
        const handleNativeAction = (event: any) => {
            const { title, type, role, orderId } = event.detail;
            if (currentUser) {
                console.log(`🚀 Native Notification Action Triggered: ${title} (${type})`);
                routeNotification({ title, type, role, orderId });
            }
        };

        window.addEventListener('yumii:notification:action', handleNativeAction);
        return () => window.removeEventListener('yumii:notification:action', handleNativeAction);
    }, [currentUser, routeNotification]);


    // Handle Hardware Back Button (Android)
    useEffect(() => {
        const handleNativeBackButton = async () => {
            console.log("🎮 Hardware Back Button Pressed - Screen:", view.screen);
            
            // 1. Close Cart first
            if (isCartOpen) {
                console.log("🛒 Closing Cart via Back Button");
                setIsCartOpen(false);
                return;
            }

            // 2. Dispatch custom event for modals buried in individual screens
            // This allows components like CategoryModal or LiveTrackingModal to intercept the back button
            const backEvent = new CustomEvent('yumii:hardwareBack', { cancelable: true });
            window.dispatchEvent(backEvent);
            if (backEvent.defaultPrevented) {
                console.log("🛡️ Internal Modal intercepted the back button");
                return;
            }

            // 3. Custom Navigation logic based on current screen
            // If on a sub-screen, we go back to the "main" screen of that role
            
            // Client Screens -> Home
            if (['restaurants', 'orders', 'account', 'restaurantDetail'].includes(view.screen)) {
                console.log("🏠 Back to Home from Client Screen");
                setView({ screen: 'home' });
                return;
            }

            // Admin Screens -> Dashboard
            if (view.screen.startsWith('admin') && view.screen !== 'adminDashboard') {
                console.log("📊 Back to Admin Dashboard");
                setView({ screen: 'adminDashboard' });
                return;
            }

            // Driver Screens -> Dashboard
            if (view.screen.startsWith('livreur') && view.screen !== 'livreurDashboard') {
                console.log("🚚 Back to Driver Dashboard");
                setView({ screen: 'livreurDashboard' });
                return;
            }

            // Super Admin -> Dashboard
            if (view.screen === 'superAdmin' && superAdminTab !== 'dashboard') {
                console.log("👑 Back to SuperAdmin Dashboard");
                setSuperAdminTab('dashboard');
                return;
            }

            // 4. Final Fallback: Exit or Minimize
            // We only exit if we are already on a main dashboard
            const isAtHome = view.screen === 'home' || view.screen === 'adminDashboard' || view.screen === 'livreurDashboard';
            
            if (isAtHome) {
                console.log("🚪 Already at Home/Dashboard, minimizing app...");
                App.minimizeApp(); // Use minimize instead of exit for better UX
            } else {
                // Last ditch effort: history back if possible, otherwise home
                if (window.history.length > 2) {
                    console.log("⬅️ Native History Back");
                    window.history.back();
                } else {
                    setView({ screen: 'home' });
                }
            }
        };

        const backListener = App.addListener('backButton', handleNativeBackButton);
        
        return () => {
            backListener.then(l => l.remove());
        };
    }, [isCartOpen, view, superAdminTab, lastBaseScreen, setIsCartOpen, setView, currentUser]);


    // Initial Auth Check (Pizza loader to match index.html)
    if (authLoading || isInitialCheck) {
        return (
            <div className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden bg-gray-900">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&q=80&w=1000"
                        alt="Loading Background"
                        className="w-full h-full object-cover opacity-90"
                    />
                </div>
                <div className="absolute inset-0 z-10 bg-green-900/60 mix-blend-overlay backdrop-blur-[1px]"></div>
                <div className="absolute inset-0 z-10 bg-black/20"></div>
                <div className="relative z-20 flex flex-col items-center text-center p-4">
                    <div className="h-24 md:h-32 mb-8 animate-pulse drop-shadow-2xl">
                        <img src="/logo.svg" alt="Yumii" className="h-full w-auto object-contain" />
                    </div>
                    <p className="text-white text-xl md:text-2xl font-black italic tracking-widest animate-pulse drop-shadow-lg">
                        MIAM MIAM...
                    </p>
                    <div className="mt-12 w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    // Client Data Loading (PIZZA) - Only for CLIENT role when data is loading
    const showPizzaLoading = currentUser?.role === 'client' && dataLoading && view.screen === 'home';

    if (showPizzaLoading) {
        return (
            <div className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden bg-gray-900">
                {/* Image de fond Pizza */}
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&q=80&w=1000"
                        alt="Loading Background"
                        className="w-full h-full object-cover opacity-90"
                    />
                </div>

                {/* Filtre VERT en surimpression (modifié de rouge à vert) */}
                <div className="absolute inset-0 z-10 bg-green-900/60 mix-blend-overlay backdrop-blur-[1px]"></div>
                {/* Second filtre pour assombrir légèrement et garantir la lisibilité du texte */}
                <div className="absolute inset-0 z-10 bg-black/20"></div>

                {/* Contenu */}
                <div className="relative z-20 flex flex-col items-center text-center p-4">
                    <div className="h-24 md:h-32 mb-8 animate-pulse drop-shadow-2xl">
                        <img src="/logo.svg" alt="Yumii" className="h-full w-auto object-contain" />
                    </div>
                    <p className="text-white text-xl md:text-2xl font-black italic tracking-widest animate-pulse drop-shadow-lg">
                        MIAM MIAM...
                    </p>

                    {/* Spinner discret */}
                    <div className="mt-12 w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    // Si on est en mode récupération de mot de passe (clic sur lien email), on force l'écran de mise à jour
    if (isPasswordRecovery) {
        return (
            <div className="font-sans antialiased text-gray-800 bg-white min-h-screen">
                <UpdatePasswordScreen />
            </div>
        );
    }

    // --- GESTION DU FLUX D'AUTHENTIFICATION (SI NON CONNECTÉ) ---
    const renderAuthFlow = () => {
        switch (authStep) {
            case 'welcome':
                return <WelcomeScreen onNext={() => setAuthStep('role_select')} />;
            case 'role_select':
                return (
                    <RoleSelectionScreen
                        onSelectFlow={(flow) => {
                            setAuthStep(flow === 'client' ? 'auth_client' : 'auth_pro');
                        }}
                    />
                );
            case 'auth_client':
                return (
                    <ClientAuthScreen
                        onBack={() => setAuthStep('role_select')}
                        onForgotPassword={() => setAuthStep('forgot_password')}
                        onSwitchToPro={() => setAuthStep('auth_pro')}
                    />
                );
            case 'auth_pro':
                return (
                    <ProAuthScreen
                        onBack={() => setAuthStep('role_select')}
                        onForgotPassword={() => setAuthStep('forgot_password')}
                        onSwitchToClient={() => setAuthStep('auth_client')}
                    />
                );
            case 'forgot_password':
                return (
                    <ForgotPasswordScreen onBack={() => setAuthStep('role_select')} />
                );
            default:
                return <WelcomeScreen onNext={() => setAuthStep('role_select')} />;
        }
    };

    // --- RENDU DES ÉCRANS DE L'APP (SI CONNECTÉ) ---
    const renderAppScreen = () => {
        console.log("📍 [DEBUG] renderAppScreen - Current User Role:", currentUser?.role);

        if (currentUser?.role === 'super_admin') {
            switch(superAdminTab) {
                case 'restaurants': return <SuperAdminRestaurants />;
                case 'drivers': return <SuperAdminDrivers />;
                case 'payments': return <SuperAdminPayments />;
                case 'profile': return <SuperAdminProfile />;
                case 'dashboard':
                default: return <SuperAdminDashboard onNavigate={setSuperAdminTab} />;
            }
        }

        if (currentUser?.role === 'restaurateur') {
            // Redirection vers l'onboarding si pas de restaurant lié OU si le statut est "incomplete"
            if (!currentUser.restaurantId || currentUser.restaurantStatus === 'incomplete') {
                return <RestaurateurOnboardingScreen />;
            }

            if (currentUser.restaurantStatus !== 'approved' && currentUser.restaurantStatus !== 'suspended') {
                return <PendingValidationScreen />;
            }
            switch (view.screen) {
                case 'adminDashboard': return <AdminDashboardScreen />;
                case 'adminDishes': return <AdminDishesScreen />;
                case 'adminOrders': return <AdminOrdersScreen />;
                case 'adminReservations': return <AdminReservationsScreen />;
                case 'adminAccount': return <AdminAccountScreen />;
                case 'adminAccounting': return <AdminAccountingScreen />;
                case 'adminDrivers': return <AdminDriversScreen />;
                case 'adminSubscription': return <SubscriptionScreen />;
                case 'adminPayments': return <PaymentHistoryScreen />;
                default: return <AdminDashboardScreen />;
            }
        }

        const isDetailView = view.screen === 'restaurantDetail';

        // Base screen rendering logic
        const renderScreen = () => {
            if (currentUser?.role === 'livreur') {
                if (currentUser.livreurStatus === 'incomplete') {
                    return <LivreurOnboardingScreen />;
                }
                if (currentUser.livreurStatus !== 'approved') {
                    return <PendingValidationScreen />;
                }
                switch (view.screen) {
                    case 'livreurActiveOrder': return <LivreurActiveOrderScreen />;
                    case 'livreurDashboard': return <LivreurDashboardScreen />;
                    case 'livreurOrders': return <LivreurOrdersScreen />;
                    case 'livreurAccounting': return <LivreurAccountingScreen />;
                    case 'livreurAccount': return <LivreurAccountScreen />;
                    case 'livreurHome':
                    default: return <LivreurHomeScreen />;
                }
            }

            // Client/Admin screens
            switch (view.screen) {
                case 'restaurantDetail': 
                    return <RestaurantDetailScreen 
                        restaurantId={view.params?.id} 
                        onClose={() => setView({ screen: lastBaseScreen })} 
                    />;
                case 'restaurants': return <RestaurantsScreen />;
                case 'orders': return <OrderScreen />;
                case 'account': return <AccountScreen />;
                case 'home':
                default: return <HomeScreen />;
            }
        };

        // Detect if profile completion is needed
        const needsProfileCompletion = profileLoaded && !authLoading && currentUser &&
            currentUser.roleFetched && !hasDismissedProfileModal && currentUser.role === 'client' &&
            (currentUser.needsProfileCompletion === true || 
             (!currentUser.phone || currentUser.phone.trim() === '' || !currentUser.wilaya || currentUser.wilaya.trim() === ''));

        return (
            <div className="relative h-full w-full">
                {/* Profile Completion Modal */}
                {needsProfileCompletion && (
                    <ProfileCompletionModal 
                        onComplete={() => { }} 
                        onClose={() => {
                            sessionStorage.setItem('yumii_profile_modal_dismissed', 'true');
                            setHasDismissedProfileModal(true);
                        }}
                    />
                )}

                {/* Main Screen Content */}
                <div className="min-h-screen">
                    {renderScreen()}
                </div>
            </div>
        );
    };


    const isAuthImmersive = (!currentUser || !currentUser?.role) && ['welcome', 'role_select', 'auth_client', 'auth_pro', 'forgot_password'].includes(authStep);

    if ((!currentUser || !currentUser?.role) && isAuthImmersive) {
        return (
            <div className="font-sans antialiased text-gray-800 bg-white min-h-screen">
                {renderAuthFlow()}
            </div>
        );
    }

    // Role-based visibility
    const isRestaurateur = currentUser?.role === 'restaurateur';
    const isLivreur = currentUser?.role === 'livreur';
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
    const isClient = currentUser?.role === 'client';

    const showAdminNav = isRestaurateur && (currentUser?.restaurantStatus === 'approved' || currentUser?.restaurantStatus === 'suspended');
    const showClientNav = isClient;
    const isSuperAdmin = currentUser?.role === 'super_admin';
    const showLivreurNav = isLivreur && currentUser?.livreurStatus === 'approved' && view.screen !== 'livreurActiveOrder';

    return (
        <div className="font-sans antialiased text-gray-800 relative min-h-screen bg-gray-50">
            {authError && (
                <div className="fixed top-20 left-4 right-4 z-[9999] bg-red-600 text-white p-4 rounded-xl shadow-2xl flex flex-col gap-2 animate-fade-in">
                    <div className="flex justify-between items-center">
                        <span className="font-bold">Erreur d'authentification</span>
                        <button onClick={() => window.location.reload()} className="bg-white text-red-600 px-3 py-1 rounded-lg font-bold text-xs uppercase">Rafraîchir</button>
                    </div>
                    <p className="text-sm opacity-90">{authError}</p>
                </div>
            )}
            {showSuccessNotification && (
                // Garder Vert pour le succès (Notification)
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] animate-bounce-in">
                    <div className="bg-green-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3">
                        <div className="bg-white/20 p-2 rounded-full">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg">Félicitations ! 🎉</h3>
                            <p className="text-green-100 text-sm">Votre restaurant a été validé.</p>
                        </div>
                    </div>
                </div>
            )}

            <main
                className={`pb-20 bg-gray-50 min-h-screen ${(currentUser?.role === 'restaurateur' && currentUser?.restaurantStatus !== 'approved') ? 'pt-safe-top' : (['home', 'restaurantDetail'].includes(view.screen) ? '' : 'pt-safe-top')}`}
                style={(currentUser?.role === 'restaurateur' && currentUser?.restaurantStatus !== 'approved') || !['home', 'restaurantDetail'].includes(view.screen) ? { paddingTop: 'max(env(safe-area-inset-top), 20px)' } : {}}
            >
                {currentUser ? renderAppScreen() : renderAuthFlow()}
            </main>
            {showAdminNav && <AdminBottomNav />}
            {showClientNav && <BottomNav />}
            {isSuperAdmin && <SuperAdminBottomNav activeTab={superAdminTab} onTabChange={setSuperAdminTab} />}
            {currentUser?.role === 'livreur' && currentUser?.livreurStatus === 'approved' && view.screen !== 'livreurActiveOrder' && <LivreurBottomNav />}
            {showAdminNav && view.screen === 'adminDashboard' && <InstallPrompt />}
            <PermissionModal />
            <CartOverlay />
            <AlarmOverlay />

            {/* Floating "Voir panier" Button for Clients */}
            <AnimatePresence>
                {currentUser?.role === 'client' && totalItems > 0 && !isCartOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[1000] w-full max-w-[200px] px-4"
                    >
                        <button
                            onClick={() => setIsCartOpen(true)}
                            className="w-full py-3.5 bg-red-600 text-white font-black rounded-full shadow-[0_8px_30px_rgba(220,38,38,0.4)] border-2 border-white/20 active:scale-95 transition-all text-[11px] uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            Voir Panier ({totalItems})
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MainApp;